/**
 * PDF OCR Demo Page - Pagina pubblica per test OCR
 * URL: studio.oystar.ai/pdf
 * Accessibile senza login per demo e test
 */

import { useState, useRef } from 'react';
import { API_URL } from '../config';
import { FileText, Loader, Eye, Upload, Download } from 'lucide-react';

type OCRMethod = 'tesseract' | 'easyocr' | 'paddleocr' | 'claude-vision' | 'deepseek-ocr';

export default function PDFOCRDemo() {
  console.log('📄 PDFOCRDemo component mounted!');
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [ocrMethod, setOcrMethod] = useState<OCRMethod>('easyocr');  // EasyOCR raccomandato!
  const [isProcessing, setIsProcessing] = useState(false);
  const [ocrResult, setOcrResult] = useState<string>('');
  const [processingTime, setProcessingTime] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      setOcrResult('');
      
      // Se DeepSeek OCR è selezionato, mostra subito l'alert
      if (ocrMethod === 'deepseek-ocr') {
        alert('⚠️ SERVIZIO OUT - DeepSeek OCR richiede GPU e non è disponibile.\n\nUsa invece EasyOCR (raccomandato, gratis) o Claude Vision (premium).');
        // Reset alla selezione precedente o default
        setOcrMethod('easyocr');
      }
    } else {
      alert('❌ Seleziona un file PDF valido!');
    }
  };

  const handleProcessPDF = async () => {
    if (!selectedFile) {
      alert('❌ Nessun file selezionato!');
      return;
    }

    // Blocca DeepSeek OCR
    if (ocrMethod === 'deepseek-ocr') {
      alert('⚠️ SERVIZIO OUT - DeepSeek OCR richiede GPU e non è disponibile.\n\nUsa invece EasyOCR (raccomandato, gratis) o Claude Vision (premium).');
      return;
    }

    setIsProcessing(true);
    setOcrResult('');
    const startTime = Date.now();

    try {
      // STEP 1: Upload PDF
      const formData = new FormData();
      formData.append('files', selectedFile);

      const uploadResponse = await fetch(`${API_URL}/api/flow/kb/upload-pdf-only`, {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.status}`);
      }

      const uploadData = await uploadResponse.json();
      const filename = uploadData.files[0]?.filename || selectedFile.name;

      // STEP 2: Estrai con metodo selezionato (ESATTAMENTE come KnowledgeBasePanel!)
      const requestData = {
        filename: filename,
        method: ocrMethod,
        language: 'ita+eng',
        max_pages: ocrMethod === 'tesseract' ? 50 : 10
      };

      console.log('🔍 Invio richiesta OCR:', requestData);

      const response = await fetch(`${API_URL}/api/ocr/extract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ Risultato OCR ricevuto:', data);

      // Formatta risultato
      let resultContent = `🔍 ESTRAZIONE OCR COMPLETATA\n\n`;
      resultContent += `📄 File: ${data.filename}\n`;
      
      // Nome metodo leggibile
      const methodNames: Record<string, string> = {
        'tesseract': '📝 Tesseract OCR',
        'easyocr': '🎯 EasyOCR (Deep Learning)',
        'paddleocr': '🚀 PaddleOCR',
        'claude-vision': '🤖 Claude Sonnet 4 (Cloud live)',
        'deepseek-ocr': '🔮 DeepSeek OCR (HuggingFace)'
      };
      
      resultContent += `⚙️ Metodo: ${methodNames[data.method] || data.method}\n`;
      resultContent += `📊 Pagine processate: ${data.pages_processed}${data.total_pages ? `/${data.total_pages}` : ''}\n`;
      resultContent += `📏 Testo estratto: ${data.text_length.toLocaleString()} caratteri\n`;
      
      if (data.model) {
        resultContent += `🤖 Modello: ${data.model}\n`;
      }
      
      if (data.note) {
        resultContent += `💡 ${data.note}\n`;
      }
      
      resultContent += `\n`;
      
      resultContent += `📝 TESTO ESTRATTO (COMPLETO):\n`;
      resultContent += `${'='.repeat(50)}\n`;
      // Usa FULL_TEXT (tutto il testo estratto, non solo preview!)
      resultContent += data.full_text || data.extracted_text || data.text_preview || 'Nessun testo estratto';

      setOcrResult(resultContent);
      setProcessingTime((Date.now() - startTime) / 1000);
      
      alert(`✅ Estrazione completata!\n\n${data.pages_processed} pagine\n${data.text_length.toLocaleString()} caratteri`);

    } catch (error: any) {
      console.error('❌ Errore processing:', error);
      setOcrResult(`❌ ERRORE:\n${error.message}`);
      alert(`❌ Errore: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadText = () => {
    if (!ocrResult) return;
    
    const blob = new Blob([ocrResult], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedFile?.name || 'document'}_extracted.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-800 mb-2">
                📄 OYSTAR PDF OCR Demo
              </h1>
              <p className="text-gray-600">
                Testa gratuitamente l'estrazione testo da PDF con diversi metodi OCR
              </p>
            </div>
            <a
              href="/"
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold rounded-lg transition-all shadow-lg"
            >
              🏠 Torna a Studio
            </a>
          </div>

          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>ℹ️ Pagina Demo Pubblica</strong> - Prova le funzionalità OCR di OYSTAR senza registrazione.
              Per accesso completo a tutte le funzionalità, <a href="/" className="underline font-semibold">accedi a OYSTAR Studio</a>.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pannello Upload e Configurazione */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <Upload className="w-6 h-6 mr-2 text-blue-600" />
              1. Carica PDF e Seleziona Metodo
            </h2>

            {/* Upload PDF */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                📎 Seleziona PDF:
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none p-2"
              />
              {selectedFile && (
                <p className="text-sm text-green-600 mt-2">
                  ✅ {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>

            {/* Selezione Metodo */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                🔧 Metodo di Estrazione:
              </label>
              <select
                value={ocrMethod}
                onChange={(e) => {
                  const newMethod = e.target.value as OCRMethod;
                  if (newMethod === 'deepseek-ocr') {
                    alert('⚠️ SERVIZIO OUT - DeepSeek OCR richiede GPU e non è disponibile.\n\nUsa invece EasyOCR (raccomandato, gratis) o Claude Vision (premium).');
                    return; // Non cambia la selezione
                  }
                  setOcrMethod(newMethod);
                }}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <optgroup label="🆓 Metodi Gratuiti">
                  <option value="easyocr">🎯 EasyOCR (Raccomandato - Migliore Qualità)</option>
                  <option value="tesseract">📝 Tesseract OCR (Veloce)</option>
                  <option value="paddleocr">🚀 PaddleOCR (Layout/Tabelle)</option>
                </optgroup>
                <optgroup label="💎 AI Cloud Live Premium">
                  <option value="claude-vision">🤖 Claude Sonnet 4 (Top Quality)</option>
                  <option value="deepseek-ocr" disabled>🔮 DeepSeek OCR ⚠️ Sospeso (Richiede GPU)</option>
                </optgroup>
              </select>

              {/* Descrizioni metodi */}
              <div className="mt-3 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                {ocrMethod === 'tesseract' && (
                  <p>📝 <strong>Tesseract OCR</strong> - OCR tradizionale open-source. Veloce (1-2s/pag), ideale per documenti stampati chiari. Fino a 50 pagine.</p>
                )}
                {ocrMethod === 'easyocr' && (
                  <p>🎯 <strong>EasyOCR</strong> ⭐ RACCOMANDATO - Deep learning OCR, migliore qualità di Tesseract. Riconosce scrittura a mano. 3-5s/pag. Fino a 50 pagine.</p>
                )}
                {ocrMethod === 'paddleocr' && (
                  <p>🚀 <strong>PaddleOCR</strong> - OCR velocissimo ottimizzato per CPU. Ottimo per layout complessi e tabelle. 2-3s/pag. Fino a 50 pagine.</p>
                )}
                {ocrMethod === 'claude-vision' && (
                  <p>🤖 <strong>Claude Sonnet 4</strong> - AI cloud live Anthropic, massima precisione. Riconosce tutto: testo, scrittura a mano, diagrammi. 3-5s/pag. Limite: 10 pagine.</p>
                )}
                {ocrMethod === 'deepseek-ocr' && (
                  <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
                    <p className="text-yellow-800 font-bold mb-2">⚠️ Momentaneamente sospeso</p>
                    <p className="text-yellow-700 text-sm">
                      DeepSeek OCR richiede GPU e non è attualmente disponibile tramite API cloud standard. 
                      Usa invece <strong>EasyOCR</strong> (raccomandato, gratis, alta qualità) o <strong>Claude Vision</strong> (premium).
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Bottone Elabora */}
            <button
              onClick={handleProcessPDF}
              disabled={!selectedFile || isProcessing}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-4 px-6 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg"
            >
              {isProcessing ? (
                <>
                  <Loader className="w-5 h-5 mr-2 animate-spin" />
                  Elaborazione in corso...
                </>
              ) : (
                <>
                  <Eye className="w-5 h-5 mr-2" />
                  🔍 Elabora PDF
                </>
              )}
            </button>

            {processingTime > 0 && (
              <p className="text-sm text-gray-600 mt-3 text-center">
                ⏱️ Tempo elaborazione: <strong>{processingTime.toFixed(2)}s</strong>
              </p>
            )}
          </div>

          {/* Pannello Risultato */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                <FileText className="w-6 h-6 mr-2 text-green-600" />
                2. Risultato Estrazione
              </h2>
              {ocrResult && (
                <button
                  onClick={downloadText}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg transition-colors flex items-center gap-2"
                  title="Scarica testo estratto"
                >
                  <Download className="w-4 h-4" />
                  Scarica TXT
                </button>
              )}
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200 min-h-[500px]">
              {!ocrResult && !isProcessing && (
                <div className="text-center text-gray-400 py-20">
                  <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Carica un PDF e scegli un metodo per vedere il risultato</p>
                </div>
              )}

              {isProcessing && (
                <div className="text-center text-gray-600 py-20">
                  <Loader className="w-16 h-16 mx-auto mb-4 animate-spin text-blue-600" />
                  <p className="text-lg font-semibold">Elaborazione in corso...</p>
                  <p className="text-sm mt-2">
                    {ocrMethod === 'tesseract' && '📝 Tesseract OCR in corso...'}
                    {ocrMethod === 'easyocr' && '🎯 EasyOCR sta analizzando (3-5s/pag)...'}
                    {ocrMethod === 'paddleocr' && '🚀 PaddleOCR sta elaborando (2-3s/pag)...'}
                    {ocrMethod === 'claude-vision' && '🤖 Claude Vision (cloud live) sta analizzando...'}
                    {ocrMethod === 'deepseek-ocr' && '⚠️ DeepSeek OCR è momentaneamente sospeso'}
                  </p>
                </div>
              )}

              {ocrResult && (
                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-700">
                      📊 Caratteri estratti: <span className="text-blue-600">{ocrResult.length.toLocaleString()}</span>
                    </span>
                    <span className="text-sm text-gray-600">
                      Metodo: <strong className="text-purple-600">{ocrMethod}</strong>
                    </span>
                  </div>
                  <textarea
                    value={ocrResult}
                    readOnly
                    className="w-full h-[600px] p-4 border border-gray-300 rounded-lg font-mono text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 overflow-y-auto"
                    placeholder="Il testo estratto apparirà qui..."
                    style={{ whiteSpace: 'pre-wrap' }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Info metodi */}
        <div className="mt-6 bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">📚 Guida ai Metodi di Estrazione</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* EasyOCR - RACCOMANDATO */}
            <div className="border-4 border-green-400 rounded-lg p-6 bg-green-50 relative">
              <div className="absolute top-2 right-2 bg-green-600 text-white text-xs font-bold px-2 py-1 rounded">
                ⭐ RACCOMANDATO
              </div>
              <h4 className="font-bold text-green-800 mb-3 text-xl">🎯 EasyOCR</h4>
              <p className="text-sm text-gray-600 mb-4">Deep Learning OCR - Migliore Qualità</p>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>✅ <strong>Migliore qualità</strong> di Tesseract</li>
                <li>✅ Riconosce <strong>scrittura a mano</strong></li>
                <li>✅ <strong>Gratis</strong> e open-source</li>
                <li>✅ 80+ lingue supportate</li>
                <li>⏱️ 3-5 secondi/pagina (CPU)</li>
                <li>📄 Fino a 50 pagine</li>
              </ul>
            </div>

            {/* Tesseract */}
            <div className="border-2 border-yellow-200 rounded-lg p-6 bg-yellow-50">
              <h4 className="font-bold text-yellow-800 mb-3 text-xl">📝 Tesseract OCR</h4>
              <p className="text-sm text-gray-600 mb-4">OCR Tradizionale - Velocità Massima</p>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>✅ <strong>Velocissimo</strong> (1-2s/pag)</li>
                <li>✅ <strong>Gratis</strong> e open-source</li>
                <li>✅ 100+ lingue</li>
                <li>✅ Stabile e testato da anni</li>
                <li>⚠️ Qualità standard</li>
                <li>📄 Fino a 50 pagine</li>
              </ul>
            </div>

            {/* PaddleOCR */}
            <div className="border-2 border-blue-200 rounded-lg p-6 bg-blue-50">
              <h4 className="font-bold text-blue-800 mb-3 text-xl">🚀 PaddleOCR</h4>
              <p className="text-sm text-gray-600 mb-4">OCR Veloce - Layout & Tabelle</p>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>✅ <strong>Veloce</strong> su CPU (2-3s/pag)</li>
                <li>✅ <strong>Layout detection</strong> automatico</li>
                <li>✅ Ottimo per <strong>tabelle</strong></li>
                <li>✅ <strong>Gratis</strong> e open-source</li>
                <li>✅ 80+ lingue</li>
                <li>📄 Fino a 50 pagine</li>
              </ul>
            </div>

            {/* Claude Vision */}
            <div className="border-2 border-purple-200 rounded-lg p-5 bg-purple-50">
              <h4 className="font-bold text-purple-800 mb-2 text-lg">🤖 Claude Sonnet 4</h4>
              <p className="text-xs text-gray-600 mb-3">AI Cloud live - Precisione Massima</p>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>✅ <strong>Precisione assoluta</strong></li>
                <li>✅ Comprensione contestuale</li>
                <li>✅ Riconosce <strong>tutto</strong></li>
                <li>✅ Cloud live (3-5s/pag)</li>
                <li>💰 A pagamento (~$0.015/pag)</li>
                <li>⚠️ Limite: 10 pagine</li>
              </ul>
            </div>

            {/* DeepSeek OCR - Sospeso */}
            <div className="border-2 border-yellow-200 rounded-lg p-5 bg-yellow-50 opacity-75">
              <h4 className="font-bold text-yellow-800 mb-2 text-lg">🔮 DeepSeek OCR ⚠️</h4>
              <p className="text-xs text-yellow-700 mb-3 font-bold">Momentaneamente sospeso</p>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>❌ <strong>Non disponibile</strong> - Richiede GPU</li>
                <li>❌ Non disponibile tramite API cloud standard</li>
                <li>💡 Usa <strong>EasyOCR</strong> (raccomandato) o <strong>Claude Vision</strong> invece</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-gray-600 text-sm">
          <p>
            Powered by <strong className="text-purple-600">OYSTAR AI</strong> • 
            <a href="https://oystar.ai" className="text-blue-600 hover:underline ml-2">oystar.ai</a>
          </p>
        </div>
      </div>
    </div>
  );
}

